import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, MessageCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { deliveryCharge } from "@/lib/pricing";
import api, { taka } from "@/lib/api";

export default function Cart() {
  const { items, setQty, removeItem, subtotal, unitPrice, lineTotal } = useCart();
  const nav = useNavigate();
  const { settings } = useSettings();
  const delivery = items.length ? deliveryCharge(items, settings) : 0;

  const whatsappOrder = () => {
    const lines = items.map((i) => `• ${i.product.name_bn} — ${i.quantity} ${i.product.unit} × ${taka(unitPrice(i))} = ${taka(lineTotal(i))}`).join("\n");
    const msg = `আসসালামু আলাইকুম, আমি অর্ডার করতে চাই:\n\n${lines}\n\nসাবটোটাল: ${taka(subtotal)}\nডেলিভারি: ${taka(delivery)}\nসর্বমোট: ${taka(subtotal + delivery)}\n\nঅনুগ্রহ করে অর্ডারটি কনফার্ম করুন।`;
    const num = (settings.whatsapp || "").replace(/[^0-9]/g, "");
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <ShoppingBag size={56} className="mx-auto text-stone-300 mb-4" />
        <h2 className="text-xl font-bold text-[#1E3A2B]">আপনার কার্ট খালি</h2>
        <p className="text-stone-500 mt-1">পণ্য যোগ করে কেনাকাটা শুরু করুন</p>
        <Link to="/" className="inline-flex mt-6 bg-[#1E3A2B] text-white font-medium px-6 h-12 rounded-xl items-center" data-testid="cart-shop-btn">কেনাকাটা করুন</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <h1 className="text-2xl font-bold text-[#1E3A2B] mb-5">আমার কার্ট ({items.length})</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {items.map(({ product, quantity }) => (
            <div key={product.id} data-testid={`cart-item-${product.id}`} className="bg-white rounded-2xl border border-stone-200 p-3 flex gap-3 items-center">
              <img src={product.image || undefined} alt="" className="w-20 h-20 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#1C1917] truncate">{product.name_bn}</div>
                <div className="text-emerald-800 font-bold">{taka(unitPrice({ product, quantity }))} <span className="text-xs text-stone-500 font-normal">/ {product.unit}</span>
                  {unitPrice({ product, quantity }) < product.price && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">পাইকারি</span>}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex items-center border border-stone-300 rounded-lg">
                    <button onClick={() => setQty(product.id, quantity - 1)} className="w-8 h-8 flex items-center justify-center" data-testid={`cart-minus-${product.id}`}><Minus size={15} /></button>
                    <span className="w-10 text-center text-sm font-medium">{quantity}</span>
                    <button onClick={() => setQty(product.id, quantity + 1)} className="w-8 h-8 flex items-center justify-center" data-testid={`cart-plus-${product.id}`}><Plus size={15} /></button>
                  </div>
                  <button onClick={() => removeItem(product.id)} className="text-red-500 p-2" data-testid={`cart-remove-${product.id}`}><Trash2 size={17} /></button>
                </div>
              </div>
              <div className="font-bold text-[#1E3A2B]">{taka(lineTotal({ product, quantity }))}</div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 p-5 h-fit sticky top-24">
          <h3 className="font-bold text-[#1E3A2B] mb-4">অর্ডার সারাংশ</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-stone-600">সাবটোটাল</span><span className="font-medium">{taka(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-stone-600">ডেলিভারি চার্জ</span><span className="font-medium">{taka(delivery)}</span></div>
            <div className="border-t border-stone-200 pt-2 flex justify-between text-base font-bold text-[#1E3A2B]"><span>সর্বমোট</span><span>{taka(subtotal + delivery)}</span></div>
          </div>
          <button onClick={() => nav("/checkout")} data-testid="checkout-btn" className="mt-5 w-full bg-[#CA8A04] hover:bg-[#A16207] text-white font-medium h-12 rounded-xl flex items-center justify-center gap-2 transition-colors">
            চেকআউট করুন <ArrowRight size={18} />
          </button>
          <button onClick={whatsappOrder} data-testid="whatsapp-order-btn" className="mt-3 w-full bg-[#25D366] hover:bg-[#1DA851] text-white font-medium h-12 rounded-xl flex items-center justify-center gap-2 transition-colors">
            <MessageCircle size={18} /> হোয়াটসঅ্যাপে অর্ডার
          </button>
          <Link to="/" className="mt-3 block text-center text-sm text-emerald-700 font-medium">আরও কিনুন</Link>
        </div>
      </div>
    </div>
  );
}
